import { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Activity,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  XCircle,
  HelpCircle,
  Database,
  Train,
  Ship,
  Anchor,
  Mail,
  Brain,
  Link2,
  Clock,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Phone,
  Truck,
} from "lucide-react";


// Map integration names to icons
const integrationIcons = {
  database: Database,
  metrans: Train,
  searates: Ship,
  port_tracking: Anchor,
  carrier_tracking: Ship,
  cma_cgm: Ship,
  email_service: Mail,
  twilio_sms: Phone,
  twilio_whatsapp: MessageSquare,
  lcl_status_api: Link2,
  freightlinks_api: Link2,
  openai: Brain,
  transporeon: Truck,
  timocom: Truck,
};

// Status indicator component
const StatusIndicator = ({ status }) => {
  const configs = {
    healthy: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-100", label: "Healthy" },
    degraded: { icon: AlertCircle, color: "text-amber-500", bg: "bg-amber-100", label: "Degraded" },
    down: { icon: XCircle, color: "text-red-500", bg: "bg-red-100", label: "Down" },
    unknown: { icon: HelpCircle, color: "text-gray-400", bg: "bg-gray-100", label: "Unknown" },
  };

  const config = configs[status] || configs.unknown;
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.bg}`}>
      <Icon className={`h-4 w-4 ${config.color}`} />
      <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
    </div>
  );
};

// Large status light
const StatusLight = ({ status }) => {
  const colors = {
    healthy: "bg-green-500 shadow-green-500/50",
    degraded: "bg-amber-500 shadow-amber-500/50",
    critical: "bg-red-500 shadow-red-500/50",
    down: "bg-red-500 shadow-red-500/50",
    unknown: "bg-gray-400 shadow-gray-400/50",
  };

  return (
    <div className={`w-4 h-4 rounded-full ${colors[status] || colors.unknown} shadow-lg animate-pulse`} />
  );
};

// Integration card component
const IntegrationCard = ({ integration }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = integrationIcons[integration.name] || Activity;

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            integration.status === "healthy" ? "bg-green-50" :
            integration.status === "degraded" ? "bg-amber-50" :
            integration.status === "down" ? "bg-red-50" : "bg-gray-50"
          }`}>
            <Icon className={`h-5 w-5 ${
              integration.status === "healthy" ? "text-green-600" :
              integration.status === "degraded" ? "text-amber-600" :
              integration.status === "down" ? "text-red-600" : "text-gray-600"
            }`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{integration.display_name}</h3>
            {integration.response_time_ms && (
              <p className="text-xs text-slate-500">{integration.response_time_ms}ms response</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusIndicator status={integration.status} />
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t bg-slate-50 p-4 space-y-2">
          {integration.last_check && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-slate-400" />
              <span className="text-slate-600">Last checked: </span>
              <span className="text-slate-800">{new Date(integration.last_check).toLocaleString()}</span>
            </div>
          )}
          {integration.last_success && (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-slate-600">Last success: </span>
              <span className="text-slate-800">{new Date(integration.last_success).toLocaleString()}</span>
            </div>
          )}
          {integration.last_error && (
            <div className="flex items-start gap-2 text-sm">
              <XCircle className="h-4 w-4 text-red-500 mt-0.5" />
              <div>
                <span className="text-slate-600">Last error: </span>
                <span className="text-red-600">{integration.last_error}</span>
              </div>
            </div>
          )}
          {integration.details && (
            <div className="mt-2 p-2 bg-white rounded border">
              <p className="text-sm text-slate-600">{integration.details.message}</p>
              {integration.details.last_container && (
                <p className="text-xs text-slate-500 mt-1">Container: {integration.details.last_container}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function HealthDashboardPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchDashboard = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      const response = await axios.get(`${API_URL}/health/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error fetching health dashboard:", error);
      toast.error("Failed to fetch health status");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchDashboard();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const getOverallStatusText = (status) => {
    switch (status) {
      case "healthy":
        return "All Systems Operational";
      case "degraded":
        return "Some Systems Degraded";
      case "critical":
        return "Critical Issues Detected";
      default:
        return "Status Unknown";
    }
  };

  const getOverallStatusColor = (status) => {
    switch (status) {
      case "healthy":
        return "from-green-500 to-emerald-600";
      case "degraded":
        return "from-amber-500 to-orange-500";
      case "critical":
        return "from-red-500 to-rose-600";
      default:
        return "from-gray-500 to-slate-600";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 py-8 pb-96">
        {/* Header */}
        <div className={`bg-gradient-to-r ${getOverallStatusColor(dashboardData?.overall_status)} rounded-2xl p-8 text-white mb-8`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Activity className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Integration Health</h1>
                <p className="text-white/80 flex items-center gap-2 mt-1">
                  <StatusLight status={dashboardData?.overall_status} />
                  {getOverallStatusText(dashboardData?.overall_status)}
                </p>
              </div>
            </div>
            <div className="text-right">
              <Button
                onClick={() => fetchDashboard(true)}
                disabled={refreshing}
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {lastRefresh && (
                <p className="text-xs text-white/60 mt-2">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { 
              label: "Healthy", 
              count: dashboardData?.integrations?.filter(i => i.status === "healthy").length || 0,
              color: "text-green-600",
              bg: "bg-green-50"
            },
            { 
              label: "Degraded", 
              count: dashboardData?.integrations?.filter(i => i.status === "degraded").length || 0,
              color: "text-amber-600",
              bg: "bg-amber-50"
            },
            { 
              label: "Down", 
              count: dashboardData?.integrations?.filter(i => i.status === "down").length || 0,
              color: "text-red-600",
              bg: "bg-red-50"
            },
            { 
              label: "Unknown", 
              count: dashboardData?.integrations?.filter(i => i.status === "unknown").length || 0,
              color: "text-gray-600",
              bg: "bg-gray-50"
            },
          ].map((stat) => (
            <div key={stat.label} className={`${stat.bg} rounded-xl p-4 text-center`}>
              <p className={`text-3xl font-bold ${stat.color}`}>{stat.count}</p>
              <p className="text-sm text-slate-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Integration List */}
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Integration Status</h2>
          
          {dashboardData?.integrations?.map((integration) => (
            <IntegrationCard key={integration.name} integration={integration} />
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h4 className="font-semibold text-blue-800 mb-2">About This Dashboard</h4>
          <p className="text-sm text-blue-700">
            This dashboard monitors the health of all external integrations. Statuses are updated automatically every 60 seconds.
          </p>
          <ul className="mt-3 space-y-1 text-sm text-blue-600">
            <li>🟢 <strong>Healthy</strong>: Integration is working normally</li>
            <li>🟡 <strong>Degraded</strong>: Partial functionality or warnings</li>
            <li>🔴 <strong>Down</strong>: Integration is not working - needs attention</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
